import { useId, useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronRight, ChevronDown } from "lucide-react";
import MenuSheet from "./MenuSheet";
import OptionList from "./OptionList";
import useOptionsMenu from "./useOptionsMenu";

export default function FormSelect({
  label,
  icon,
  value,
  onChange,
  options = [],
  placeholder,
  className = "",
  borderless = false,
  variant = "default",
  searchable,
  disabled = false,
  error,
  accent = "brand",
  ...props
}) {
  const selectId = useId();
  const currentOption = options.find((o) => o.value === value);
  const currentLabel  = currentOption?.label ?? placeholder ?? "—";

  /* ── Custom dropdown variant (sheet móvil / popover desktop) ──────────── */
  if (variant === "menu") {
    return (
      <MenuSelect
        label={label}
        icon={icon}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        className={className}
        searchable={searchable}
        disabled={disabled}
        error={error}
        accent={accent}
        {...props}
      />
    );
  }

  /* ── iOS Settings row variant ────────────────────────────────────────── */
  if (variant === "row") {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center gap-3 py-3 border-b border-slate-100/70 dark:border-slate-800/15 pointer-events-none select-none">
          {icon && <div className="text-slate-400 dark:text-slate-500 shrink-0">{icon}</div>}
          <span className="flex-1 text-[14px] text-slate-500 dark:text-slate-400">{label}</span>
          <span className="text-[14px] font-medium text-slate-700 dark:text-slate-200 mr-0.5">
            {currentLabel}
          </span>
          <ChevronRight size={13} className="text-slate-300 dark:text-slate-700 shrink-0 opacity-60" />
        </div>
        <select
          id={selectId}
          className="kv-field-control absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }

  /* ── Default + borderless variant ───────────────────────────────────── */
  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-1"
        >
          {label}
        </label>
      )}
      <div className={`flex items-center gap-3 bg-transparent dark:bg-transparent transition-all duration-200 ease-out ${
        borderless
          ? "border-b border-slate-100 dark:border-slate-800/30 px-0 py-2"
          : "border border-slate-200/60 dark:border-slate-700/50 rounded-icon px-3 py-2.5 focus-within:border-brand/50 focus-within:ring-4 focus-within:ring-brand/10 dark:focus-within:border-blue-500/50 dark:focus-within:ring-blue-500/10"
      }`}>
        {icon && (
          <div className="text-slate-400 dark:text-slate-500 shrink-0">{icon}</div>
        )}
        <select
          id={selectId}
          className="kv-field-control bg-transparent outline-none w-full text-[16px] text-slate-900 dark:text-slate-100 min-w-0 appearance-none"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {!borderless && (
          <ChevronDown
            size={16}
            className="shrink-0 text-slate-400 dark:text-slate-500 pointer-events-none"
          />
        )}
      </div>
    </div>
  );
}

/* ── matchMedia desktop ≥768px ───────────────────────────────────────────── */
function useIsDesktop() {
  const [desktop, setDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 768px)");
    const on = (e) => setDesktop(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return desktop;
}

/* ── variant="menu" — caja-trigger + dropdown custom ─────────────────────── */
function MenuSelect({
  label,
  icon,
  value,
  onChange,
  options,
  placeholder,
  className,
  searchable,
  disabled,
  error,
  accent,
  ...rest
}) {
  const labelId = useId();
  const listboxId = useId();
  const [query, setQuery] = useState("");
  const isDesktop = useIsDesktop();
  const showSearch = searchable ?? options.length >= 8;

  const filtered = useMemo(() => {
    if (!showSearch || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, query, showSearch]);

  const selectedOption = options.find((o) => o.value === value);

  const menu = useOptionsMenu({
    optionCount: filtered.length,
    onSelect: (i) => {
      const o = filtered[i];
      if (o) {
        onChange(o.value);
        menu.close();
      }
    },
    popover: isDesktop,
  });

  // Limpiar búsqueda al cerrar
  useEffect(() => {
    if (!menu.open) setQuery("");
  }, [menu.open]);

  const pick = (val) => {
    onChange(val);
    menu.close();
  };

  const selectedIdx = filtered.findIndex((o) => o.value === value);

  const triggerCls = error
    ? "w-full flex items-center gap-3 border border-red-400/70 dark:border-red-500/50 rounded-icon bg-transparent dark:bg-transparent px-3 py-2.5 transition-all duration-200 ease-out focus:outline-none focus:ring-4 focus:ring-red-500/10"
    : "w-full flex items-center gap-3 border border-slate-200/60 dark:border-slate-700/50 rounded-icon bg-transparent dark:bg-transparent px-3 py-2.5 transition-all duration-200 ease-out focus:outline-none focus:border-brand/50 focus:ring-4 focus:ring-brand/10 dark:focus:border-blue-500/50 dark:focus:ring-blue-500/10";

  const list = (
    <OptionList
      options={filtered}
      value={value}
      activeIndex={menu.activeIndex}
      onPick={pick}
      onHover={menu.setActiveIndex}
      accent={accent}
      showSearch={showSearch}
      query={query}
      onQuery={setQuery}
      autoFocusSearch={isDesktop}
      listboxId={listboxId}
      onKeyDown={menu.onKeyDown}
    />
  );

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label
          id={labelId}
          className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-1"
        >
          {label}
        </label>
      )}

      <div
        ref={menu.triggerRef}
        {...rest}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={menu.open}
        aria-controls={listboxId}
        aria-labelledby={label ? labelId : undefined}
        aria-disabled={disabled || undefined}
        onClick={() =>
          !disabled && (menu.open ? menu.close() : menu.openMenu(selectedIdx < 0 ? 0 : selectedIdx))
        }
        onKeyDown={disabled ? undefined : menu.onKeyDown}
        className={`${triggerCls} cursor-pointer ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      >
        {icon && <span className="text-slate-400 dark:text-slate-500 shrink-0">{icon}</span>}
        <span
          className={`flex-1 min-w-0 text-left text-[16px] truncate ${
            selectedOption
              ? "text-slate-900 dark:text-slate-100"
              : "text-slate-400 dark:text-slate-500"
          }`}
        >
          {selectedOption ? selectedOption.label : placeholder ?? "Seleccionar…"}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${
            menu.open ? "rotate-180" : ""
          }`}
        />
      </div>

      {error && (
        <p className="text-[12px] text-red-500 dark:text-red-400 ml-1">{error}</p>
      )}

      {/* MÓVIL: hojita ligera auto-height (hugea en iOS) */}
      {!isDesktop && (
        <MenuSheet
          isOpen={menu.open}
          onClose={menu.close}
          title={label || "Seleccionar"}
        >
          {list}
        </MenuSheet>
      )}

      {/* DESKTOP: popover via portal (position:fixed, no se clipa) */}
      {isDesktop &&
        menu.open &&
        menu.position &&
        createPortal(
          <div
            ref={menu.panelRef}
            style={{
              position: "fixed",
              left: menu.position.left,
              width: menu.position.width,
              top: menu.position.top,
              bottom: menu.position.bottom,
              maxHeight: menu.position.maxHeight,
              zIndex: 70,
            }}
            className="overflow-y-auto overscroll-contain bg-page-bg rounded-[16px] border border-slate-200/60 dark:border-slate-700/50 shadow-[0_12px_40px_rgba(0,0,0,0.12)] dark:shadow-none p-1.5 animate-in fade-in zoom-in-95 duration-150"
          >
            {list}
          </div>,
          document.body
        )}
    </div>
  );
}
